'use client'
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";

const hashtags = [
  "#mindcare", "#vindecare", "#echilibru", "#terapie", "#procesNuMoment",
  "#împreunăSchimbăm", "#accespentrufiecare", "#fiecarecontează",
  "#respiră", "#terapiacuimpact", "#grijăFărăBariere", "#solidaritate",
  "#putereÎnEmpatie", "#emoțieDeschisă", "#creștere", "#autocunoaștere",
  "#psihoterapie", "#sănătateMintală", "#mindtalecare", "#vindecareÎmpreună",
  "#reconectare", "#calm", "#claritate", "#echilibruEmoțional", "#fiiPrezent",
];

export default function HashtagLayer() {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;

    const container = ref.current;
    if (!container) return;

    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const renderHashtags = () => {
      container.innerHTML = "";

      const footer = document.querySelector("footer");
      let footerTop = document.documentElement.scrollHeight;
      if (footer) {
        const rect = footer.getBoundingClientRect();
        footerTop = rect.top + window.scrollY;
      }

      const maxHeight = Math.max(0, footerTop - 200);
      container.style.height = `${footerTop}px`;
      container.style.overflow = "hidden";

      const forbiddenEls = document.querySelectorAll(
        "h1, h2, h3, p, button, a, img, input, form, nav, header, footer, .no-hashtag-zone"
      );

      const forbiddenRects = Array.from(forbiddenEls).map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          x: rect.left,
          y: rect.top + window.scrollY,
          w: rect.width + 40,
          h: rect.height + 40,
        };
      });

      const placed: { x: number; y: number; w: number; h: number }[] = [];
      const palette = [
        "rgba(38, 70, 83, 0.12)", // dark teal
        "rgba(42, 157, 143, 0.12)", // greenish
        "rgba(233, 196, 106, 0.15)", // yellow
        "rgba(244, 162, 97, 0.12)", // orange
        "rgba(231, 111, 81, 0.12)", // coral
      ];

      const isOverlapping = (r1: any, r2: any) =>
        !(
          r2.x > r1.x + r1.w + 20 ||
          r2.x + r2.w + 20 < r1.x ||
          r2.y > r1.y + r1.h + 20 ||
          r2.y + r2.h + 20 < r1.y
        );

      const placeHashtag = (text: string) => {
        const tag = document.createElement("span");
        const color = palette[Math.floor(Math.random() * palette.length)];
        tag.textContent = text;
        tag.setAttribute("aria-hidden", "true");

        tag.style.cssText = `
            position: absolute;
            color: ${color};
            font-size: 0.875rem;
            font-weight: 500;
            user-select: none;
            pointer-events: none;
            z-index: 1;
            mix-blend-mode: multiply;
            opacity: 1;
            animation: float ${6 + Math.random() * 6}s ease-in-out infinite;
            animation-delay: ${Math.random() * 4}s;
            filter: blur(0.3px);
          `;

        container.appendChild(tag);

        const tagRect = tag.getBoundingClientRect();
        const w = tagRect.width || 80;
        const h = tagRect.height || 24;

        let x = 0,
          y = 0,
          valid = false,
          tries = 0;

        while (!valid && tries < 300) {
          tries++;
          x = Math.random() * (window.innerWidth - w);
          y = Math.random() * maxHeight;

          const candidate = { x, y, w, h };
          const overlapsForbidden = forbiddenRects.some((r) =>
            isOverlapping(candidate, r)
          );
          const overlapsOther = placed.some((p) =>
            isOverlapping(candidate, p)
          );

          if (!overlapsForbidden && !overlapsOther) valid = true;
        }

        if (valid) {
          tag.style.left = `${x}px`;
          tag.style.top = `${y}px`;
          placed.push({ x, y, w, h });
        } else {
          tag.remove();
        }
      };

      const pageHeight = Math.max(footerTop, window.innerHeight);
      const density = Math.max(12, Math.min(50, Math.floor(pageHeight / 160)));

      const shuffled = [...hashtags].sort(() => Math.random() - 0.5);
      shuffled.slice(0, density).forEach(placeHashtag);
    };

    const scheduleRender = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(renderHashtags);
      }, 200);
    };

    const resizeObserver = new ResizeObserver(scheduleRender);
    resizeObserver.observe(document.body);
    window.addEventListener("resize", scheduleRender);

    scheduleRender();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleRender);
    };
  }, [pathname, mounted]);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      role="presentation"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
}
